import { useEffect, useState } from "react";
import { BsFilter } from "react-icons/bs";
import Select from "react-select";
import { useFetch } from "../../hooks/queries";
import { toast } from "react-toastify";
import moment from "moment";

const Filter = ({
  isLoading,
  onApply,
  applyButtonClassName = "btn btn-sm w-100 cts-btn-primary-3",
  showDateRange = false,
  startDate,
  endDate,
  onChangeStartDate,
  onChangeEndDate,
  minStartDate = "2025-08-25",
  showExport = false,
  onExport,
  exporting = false,
  exportLabel = "Export",
  exportButtonClassName = "btn btn-sm w-100 cts-btn-primary-3",
  canExportStudents = false,
  canExportMiddleSchool = false,
  canExportHighSchool = false,
  onExportStudents,
  onExportMiddleSchool,
  onExportHighSchool,
  exportingStudent = false,
  exportingMiddleSchool = false,
  exportingHighSchool = false,
  onViewMiddleSchool,
  onViewHighSchool,
  viewingMiddleSchool = false,
  viewingHighSchool = false,
}) => {
  const [schools, setSchools] = useState([]);
  const [operators, setOperators] = useState([]);

  const [selectedTempSchool, setSelectedTempSchool] = useState({
    label: "All Schools",
    value: 0,
  });

  const [selectedTempOperator, setSelectedTempOperator] = useState({
    label: "N/A",
    value: 0,
  });

  const [dateError, setDateError] = useState("");

  const {
    data: operatorsResponse,
    isPending: operatorsPending,
    isLoading: operatorsLoading,
  } = useFetch({
    queryKey: ["filter-operators"],
    url: process.env.REACT_APP_API_URL + "Operators/GetDLLOperators",
    method: "GET",
  });

  useEffect(() => {
    if (!operatorsResponse || operatorsResponse.statusCode !== 200) return;

    const content = operatorsResponse.content || [];
    const storedOperator = localStorage.getItem("OTsReD");

    if (storedOperator != null && storedOperator !== "null") {
      const filtered = content.filter(
        (i) => String(i.value) === String(storedOperator)
      );
      setOperators(filtered);
      if (filtered.length > 0) {
        setSelectedTempOperator(filtered[0]);
      } else {
        setSelectedTempOperator({ label: "N/A", value: 0 });
      }
    } else {
      setOperators(content);
    }
  }, [operatorsResponse]);

  const {
    data: schoolsResponse,
    isPending: schoolsPending,
    isLoading: schoolsLoading,
  } = useFetch({
    queryKey: ["filter-schools"],
    url: process.env.REACT_APP_API_URL + "Schools/GetDLLSchools",
    method: "GET",
  });

  useEffect(() => {
    if (!schoolsResponse || schoolsResponse.statusCode !== 200) return;

    const content = schoolsResponse.content || [];
    const storedSchool = localStorage.getItem("STsReD");
    const storedOperator = localStorage.getItem("OTsReD");

    if (storedSchool != null && storedSchool !== "null") {
      const filtered = content.filter(
        (i) => String(i.value) === String(storedSchool)
      );
      setSchools([{ label: "All Schools", value: 0 }, ...filtered]);
      if (filtered.length > 0) {
        setSelectedTempSchool(filtered[0]);
      } else {
        setSelectedTempSchool({ label: "All Schools", value: 0 });
      }
    } else if (storedOperator != null && storedOperator !== "null") {
      const filtered = content.filter(
        (i) => String(i.operatorId) === String(storedOperator)
      );
      setSchools([{ label: "All Schools", value: 0 }, ...filtered]);
      localStorage.removeItem("STsReD");
    } else {
      setSchools([{ label: "All Schools", value: 0 }, ...content]);
    }
  }, [schoolsResponse]);

  const validateDates = () => {
    if (!showDateRange || !startDate || !endDate) {
      setDateError("");
      return true;
    }

    const start = moment(startDate);
    const end = moment(endDate);

    if (end.isBefore(start)) {
      setDateError("End Date cannot be earlier than Start Date");
      return false;
    }

    setDateError("");
    return true;
  };

  const handleApply = () => {
    // Validate dates before applying filter
    if (!validateDates()) {
      toast.warning("Please correct the date range before applying the filter");
      return;
    }

    if (onApply) {
      onApply({
        operator: selectedTempOperator,
        school: selectedTempSchool,
      });
    }
  };

  const handleExport = (exportFn) => {
    if (exportFn) {
      exportFn({
        operator: selectedTempOperator,
        school: selectedTempSchool,
      });
    }
  };

  const hasStudentExports =
    canExportStudents || canExportMiddleSchool || canExportHighSchool;

  const storedOperatorId = localStorage.getItem("OTsReD");
  const selectedOperatorId = String(selectedTempOperator.value || 0);

  const schoolOptions =
    storedOperatorId !== "null" && storedOperatorId != null
      ? schools
      : selectedOperatorId === "0"
      ? schools
      : schools.filter(
          (f) => f.value === 0 || String(f.operatorId) === selectedOperatorId
        );

  const internalLoading =
    operatorsPending || operatorsLoading || schoolsPending || schoolsLoading;

  return (
    <div className="tool-bar">
      <div className="row">
        <div className="col-md-12">
          <h5 className="filter">
            <BsFilter /> Filter
          </h5>
          <hr />
        </div>
      </div>

      {showDateRange && (
        <div className="row mb-10">
          <div className="col-md-6">
            <div className="input">
              <label>Start Date</label>
              <input
                type="date"
                className={`form-control form-control-sm ${dateError ? 'is-invalid' : ''}`}
                value={startDate || ""}
                min={minStartDate}
                max={endDate || undefined}
                onChange={(e) => {
                  if (onChangeStartDate) {
                    const newStartDate = e.target.value;
                    onChangeStartDate(newStartDate);
                    
                    // Validate if end date exists and is before new start date
                    if (endDate && newStartDate && moment(endDate).isBefore(moment(newStartDate))) {
                      setDateError("End Date cannot be earlier than Start Date");
                    } else {
                      setDateError("");
                    }
                  }
                }}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="input">
              <label>End Date</label>
              <input
                type="date"
                className={`form-control form-control-sm ${dateError ? 'is-invalid' : ''}`}
                value={endDate || ""}
                min={startDate || minStartDate}
                onChange={(e) => {
                  if (onChangeEndDate) {
                    const newEndDate = e.target.value;
                    onChangeEndDate(newEndDate);
                    
                    // Validate immediately when end date changes
                    if (startDate && newEndDate && moment(newEndDate).isBefore(moment(startDate))) {
                      setDateError("End Date cannot be earlier than Start Date");
                    } else {
                      setDateError("");
                    }
                  }
                }}
              />
              {dateError && (
                <div className="text-warning" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                  {dateError}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {operators.length > 1 && (
        <div className="row mb-1">
          <div className="col-md-12">
            <div className="input-schools">
              <label>Select Operator</label>
              <Select
                options={operators}
                value={selectedTempOperator}
                onChange={(e) => {
                  if (e != null) {
                    setSelectedTempOperator(e);
                  } else {
                    setSelectedTempOperator({
                      label: "N/A",
                      value: 0,
                    });
                  }
                  setSelectedTempSchool({
                    label: "All Schools",
                    value: 0,
                  });
                }}
                isClearable={operators.length > 1}
              />
            </div>
          </div>
        </div>
      )}

      {schools.length > 1 && (
        <div className="row mb-3">
          <div className="col-md-12">
            <div className="input-schools">
              <label>Select School</label>
              <Select
                options={schoolOptions}
                placeholder="Select a school"
                value={selectedTempSchool}
                onChange={(e) => {
                  if (e != null) {
                    setSelectedTempSchool(e);
                  } else {
                    setSelectedTempSchool({
                      label: "All Schools",
                      value: 0,
                    });
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}

      <div className="row">
        <div className="col-md-12">
          <button
            className={applyButtonClassName}
            onClick={handleApply}
            disabled={isLoading || internalLoading || !!dateError}
          >
            {isLoading || internalLoading ? "Loading data ..." : "Apply Filter"}
          </button>
        </div>
      </div>

      {showExport && (
        <div className="row mt-2">
          <div className="col-md-12">
            <button
              className={exportButtonClassName}
              onClick={() => {
                // Validate dates before export
                if (!validateDates()) {
                  toast.warning("Please correct the date range before exporting");
                  return;
                }
                handleExport(onExport);
              }}
              disabled={exporting || !!dateError}
            >
              {exporting ? "Exporting ..." : exportLabel}
            </button>
          </div>
        </div>
      )}

      {hasStudentExports && (
        <>
          <div className="col-md-12 mt-2 mb-2">
            <hr className="my-2" />
          </div>

          {canExportStudents && (
            <div className="col-md-12 d-flex flex-column gap-2">
              <button
                className={exportButtonClassName}
                onClick={() => handleExport(onExportStudents)}
                disabled={exportingStudent}
              >
                {exportingStudent ? "Exporting data ..." : "Export CSV"}
              </button>
            </div>
          )}

          {/* {canExportMiddleSchool && (
            <div className="col-md-12 d-flex flex-column gap-2 mt-2 mb-2">
              <button
                className={exportButtonClassName}
                onClick={onExportMiddleSchool}
                disabled={true}
              >
                {exportingMiddleSchool
                  ? "Exporting data ..."
                  : "Export Middle School"}
              </button>
            </div>
          )}

          {canExportHighSchool && (
            <div className="col-md-12 d-flex flex-column gap-2 mb-2">
              <button
                className={exportButtonClassName}
                onClick={onExportHighSchool}
                disabled={true}
              >
                {exportingHighSchool
                  ? "Exporting data ..."
                  : "Export High School"}
              </button>
            </div>
          )} */}
        </>
      )}
    </div>
  );
};

export default Filter;
